import {
  IPayeeTaxFormResponse,
  IPayerTaxFormResponse
} from "@wingspanhq/payments/dist/interfaces/api/taxForm";
import { TinType } from "@wingspanhq/payments/dist/interfaces/taxForm";
import { IAddress } from "@wingspanhq/users/dist/lib/interfaces/address";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useMemo } from "react";
import { selectorAddressString } from "../../../shared/selectors/selectorAddressString";
import { selectorTaxClassificationLabel } from "../../../shared/selectors/selectorTaxClassificationLabel";
import {
  CORRECTION_ADDRESS,
  CORRECTION_AMOUNT,
  CORRECTION_TIN_OR_NAME
} from "../constants/corrections";
import {
  getPayeeFormW9Address,
  getPayeeTINorName
} from "./getDefaultValuesForCorrectionsForm";

export function useRequestCorrectionFields(
  taxForm: IPayerTaxFormResponse | IPayeeTaxFormResponse,
  correctionType: string
) {
  const fields = useMemo(() => {
    if (
      ![CORRECTION_AMOUNT, CORRECTION_ADDRESS, CORRECTION_TIN_OR_NAME].includes
    ) {
      throw new Error(`Invalid correction type: ${correctionType}`);
    }

    if (!taxForm) {
      throw new Error("Tax form is required");
    }

    if (correctionType === CORRECTION_AMOUNT) {
      return [
        {
          label: "1099 total",
          value: taxForm.data?.totalAmount
            ? formatMoney(taxForm.data.totalAmount, "USD")
            : "-"
        }
      ];
    } else if (correctionType === CORRECTION_ADDRESS) {
      const address = getPayeeFormW9Address(taxForm);
      const addressString = selectorAddressString({
        ...(address as IAddress)
      });
      return [
        {
          label: "Address",
          value: addressString
        }
      ];
    } else if (correctionType === CORRECTION_TIN_OR_NAME) {
      const originalTinData = getPayeeTINorName(taxForm);
      const identificationNumberType = originalTinData?.tinType;
      return [
        ...(identificationNumberType === TinType.Business
          ? [
              {
                label: "Business name",
                value: originalTinData.businessName ?? "-"
              }
            ]
          : [
              {
                label: "Name",
                value:
                  [originalTinData.firstName, originalTinData.lastName]
                    .filter(Boolean)
                    .join(" ") ?? "-"
              }
            ]),
        {
          label: "Federal tax classification",
          value: selectorTaxClassificationLabel(
            originalTinData.taxClassification
          )
        },
        {
          label: "Taxpayer identification number",
          value:
            identificationNumberType === TinType.Business
              ? `${originalTinData.ein ?? "-"} (EIN)`
              : "••••••••• (SSN)"
        }
      ];
    }

    return [];
  }, [correctionType, taxForm]);

  return fields;
}
